import React, { useEffect, useState } from 'react'
import {
  Card, Form, Input, Select, Button, InputNumber, DatePicker, Space, message, Typography,
  Row, Col, Table, Popconfirm,
} from 'antd'
import { PlusOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons'
import { useNavigate, useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { purchaseApi, supplierApi, productApi } from '../../services/api'

const { Title } = Typography

const SOURCE_OPTIONS = ['合同需求', '库存预警', '项目需求', '手工申请']

export default function PurchaseForm() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const editId = searchParams.get('id')
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [suppliers, setSuppliers] = useState([])
  const [products, setProducts] = useState([])
  const [items, setItems] = useState([{ key: 1, productId: undefined, quantity: 1, unitPrice: undefined, expectDate: undefined, notes: '' }])

  useEffect(() => {
    loadOptions()
    if (editId) loadPurchase(editId)
  }, [editId])

  const loadOptions = async () => {
    try {
      const [supRes, prodRes] = await Promise.all([
        supplierApi.list({ pageSize: 200 }),
        productApi.list({ pageSize: 200 }),
      ])
      setSuppliers(supRes.data || [])
      setProducts(prodRes.data || [])
    } catch {
      message.error('加载选项数据失败')
    }
  }

  const loadPurchase = async (id) => {
    setLoading(true)
    try {
      const res = await purchaseApi.get(id)
      form.setFieldsValue({
        supplierId: res.supplierId,
        source: res.source,
        sourceId: res.sourceId,
        notes: res.notes,
      })
      if (res.items?.length) {
        setItems(res.items.map((it, i) => ({
          key: i + 1,
          id: it.id,
          productId: it.productId,
          quantity: it.quantity,
          unitPrice: it.unitPrice,
          expectDate: it.expectDate,
          notes: it.notes,
        })))
      }
    } catch {
      message.error('加载采购申请失败')
      navigate('/purchases')
    } finally {
      setLoading(false)
    }
  }

  const addItem = () => {
    const newKey = Math.max(...items.map((i) => i.key), 0) + 1
    setItems([...items, { key: newKey, productId: undefined, quantity: 1, unitPrice: undefined, expectDate: undefined, notes: '' }])
  }

  const removeItem = (key) => {
    if (items.length <= 1) {
      message.warning('至少保留一条明细')
      return
    }
    setItems(items.filter((i) => i.key !== key))
  }

  const updateItem = (key, field, value) => {
    setItems(items.map((i) => (i.key === key ? { ...i, [field]: value } : i)))
  }

  const calcTotal = () => {
    return items.reduce((sum, it) => sum + (it.unitPrice || 0) * (it.quantity || 0), 0)
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      if (items.some((it) => !it.productId)) {
        message.error('请为每条明细选择产品')
        return
      }

      const payload = {
        ...values,
        items: items.map((it) => ({
          productId: it.productId,
          quantity: it.quantity,
          unitPrice: it.unitPrice,
          expectDate: it.expectDate ? it.expectDate.toISOString() : null,
          notes: it.notes,
        })),
      }

      if (editId) {
        await purchaseApi.update(editId, payload)
        message.success('采购申请已更新')
      } else {
        await purchaseApi.create(payload)
        message.success('采购申请已创建')
      }
      navigate('/purchases')
    } catch (e) {
      if (e.errorFields) return
      message.error(e?.error || '保存失败')
    }
  }

  const itemColumns = [
    {
      title: '产品', dataIndex: 'productId', width: 200,
      render: (v, record) => (
        <Select
          placeholder="选择产品"
          value={v}
          showSearch
          filterOption={(input, option) => (option?.label || '').toLowerCase().includes(input.toLowerCase())}
          options={products.map((p) => ({ label: `${p.sku} ${p.name}`, value: p.id }))}
          onChange={(val) => updateItem(record.key, 'productId', val)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '数量', dataIndex: 'quantity', width: 100,
      render: (v, record) => (
        <InputNumber
          min={1}
          value={v}
          onChange={(val) => updateItem(record.key, 'quantity', val)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '进价', dataIndex: 'unitPrice', width: 120,
      render: (v, record) => (
        <InputNumber
          prefix="¥"
          min={0}
          step={0.01}
          value={v}
          onChange={(val) => updateItem(record.key, 'unitPrice', val)}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: '小计', key: 'subtotal', width: 120,
      render: (_, record) => {
        const subtotal = (record.unitPrice || 0) * (record.quantity || 0)
        return <strong>¥{subtotal.toLocaleString()}</strong>
      },
    },
    {
      title: '期望到货', dataIndex: 'expectDate', width: 130,
      render: (v, record) => (
        <DatePicker
          value={v ? dayjs(v) : null}
          onChange={(val) => updateItem(record.key, 'expectDate', val)}
          style={{ width: '100%' }}
          placeholder="选择日期"
        />
      ),
    },
    {
      title: '备注', dataIndex: 'notes', width: 120,
      render: (v, record) => (
        <Input
          value={v}
          onChange={(e) => updateItem(record.key, 'notes', e.target.value)}
          placeholder="备注"
        />
      ),
    },
    {
      title: '操作', key: 'action', width: 60,
      render: (_, record) => (
        <Popconfirm title="删除此行？" onConfirm={() => removeItem(record.key)}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ]

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/purchases')}>返回</Button>
        <Title level={4} style={{ margin: 0 }}>{editId ? '编辑采购申请' : '新建采购申请'}</Title>
      </Space>

      <Card style={{ marginBottom: 16 }}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="supplierId" label="供应商">
                <Select
                  placeholder="选择供应商"
                  allowClear
                  showSearch
                  filterOption={(input, option) => (option?.label || '').toLowerCase().includes(input.toLowerCase())}
                  options={suppliers.map((s) => ({ label: s.name, value: s.id }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="source" label="采购来源" rules={[{ required: true, message: '请选择采购来源' }]}>
                <Select placeholder="选择来源" options={SOURCE_OPTIONS.map((s) => ({ label: s, value: s }))} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="sourceId" label="关联编号">
                <Input placeholder="合同ID/项目ID" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="备注">
            <Input.TextArea rows={2} placeholder="采购申请备注" />
          </Form.Item>
        </Form>
      </Card>

      <Card
        title="采购明细"
        extra={<Button type="dashed" icon={<PlusOutlined />} onClick={addItem}>添加产品</Button>}
      >
        <Table
          columns={itemColumns}
          dataSource={items}
          rowKey="key"
          pagination={false}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={3}>
                <strong>合计</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                <strong style={{ color: '#f5222d', fontSize: 16 }}>¥{calcTotal().toLocaleString()}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} colSpan={3} />
            </Table.Summary.Row>
          )}
        />
      </Card>

      <div style={{ textAlign: 'right', marginTop: 16 }}>
        <Space>
          <Button onClick={() => navigate('/purchases')}>取消</Button>
          <Button type="primary" loading={loading} onClick={handleSubmit}>
            {editId ? '保存修改' : '提交申请'}
          </Button>
        </Space>
      </div>
    </div>
  )
}
